import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Card,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { t } from "i18next";
import { Link } from "react-router-dom";
import { appRoutes } from "../../../../App";
import { CreateOrUpdateSurveyModal } from "../CreateOrUpdateSurvey/CreateOrUpdateSurveyModal";
import { Survey } from "../../models/Survey";
import { useStores } from "../../../shared/stores/RootStore";
import { AddIcon, CloseIcon, HamburgerIcon } from "../../../shared/views/icons";
import { CustomMenu } from "../../../shared/views/CustomMenu";
import useColors from "../../../hooks/useColors";
import { toast } from "../../../shared/views/CustomToaster";
import { FetchingStatus } from "../../stores/LoadingStore";

const MySurveysListBase: React.FC = () => {
  const { surveyStore, accountStore, loadingStore } = useStores();
  const surveyCreationModal = useDisclosure();
  const [red] = useColors(["red.400"]);

  const surveysByCity = useMemo(() => {
    const map = new Map<string, Survey[]>();

    for (const survey of surveyStore.surveys) {
      const citySurveys = map.get(survey.city.name) ?? [];
      citySurveys.push(survey);
      map.set(survey.city.name, citySurveys);
    }
    return map;
  }, [surveyStore.surveys]);

  const removeSurveyFromLibrary = (surveyId: string) => {
    surveyStore
      .removeFromLibrary(surveyId)
      .then(() => {
        toast("success", t("home:surveys.removeSurveyToast.success"));
      })
      .catch((error) => {
        if (error?.status === 409) {
          toast("error", t("home:surveys.removeSurveyToast.conflict"));
        } else {
          toast("error", t("home:surveys.removeSurveyToast.error"));
        }
      });
  };

  return (
    <VStack
      flexShrink={0}
      w={320}
      overflow="auto"
      alignItems="stretch"
      justifyContent="start"
      py={4}
      px={4}
    >
      <Stack>
        <Text flexGrow={1} px={2} fontWeight="semibold" textAlign="center">
          {t("home:surveys.title")}
        </Text>

        <HStack justifyContent={"center"}>
          <Button
            variant="ghost"
            size="sm"
            fontSize={"sm"}
            colorPalette="primary"
            onClick={surveyCreationModal.onOpen}
            disabled={
              !accountStore.selectedUserAccount?.permissions?.canManageSurveys
            }
          >
            <AddIcon /> {t("home:surveys.create")}
          </Button>
        </HStack>

        {loadingStore.fetchingState.surveysList === FetchingStatus.Fetching ? (
          <HStack p={4} justifyContent="center">
            <Spinner color="primary.500" />
          </HStack>
        ) : surveyStore.surveys.length ? (
          [...surveysByCity.entries()].map(([city, surveys]) => (
            <Card.Root variant="subtle" key={city}>
              <Text
                pt={2}
                pl={2}
                lineClamp={1}
                fontSize="2xs"
                textTransform="uppercase"
                fontWeight="semibold"
                opacity={0.6}
              >
                {city}
              </Text>
              <Stack gap={0}>
                {surveys.map((survey) => (
                  <HStack
                    gap={0}
                    key={survey.id}
                    _hover={{
                      textDecor: "underline",
                    }}
                  >
                    <Flex asChild flexGrow={1}>
                      <Link to={appRoutes.survey(survey.id)}>
                        <Text px={2} py={1} fontSize={"sm"}>
                          {survey.name}
                        </Text>
                      </Link>
                    </Flex>
                    <CustomMenu
                      items={[
                        {
                          text: t("home:surveys.removeFromLibrary"),
                          icon: <CloseIcon color={red} />,
                          onClick: () => removeSurveyFromLibrary(survey.id),
                          key: "",
                        },
                      ]}
                    >
                      <IconButton size="sm" variant="subtle">
                        <HamburgerIcon />
                      </IconButton>
                    </CustomMenu>
                  </HStack>
                ))}
              </Stack>
            </Card.Root>
          ))
        ) : (
          <Text p={2} fontSize="sm">
            {t("home:surveys.empty")}
          </Text>
        )}
      </Stack>

      {surveyCreationModal.open && (
        <CreateOrUpdateSurveyModal onClose={surveyCreationModal.onClose} />
      )}
    </VStack>
  );
};

export const MySurveysList = observer(MySurveysListBase);
